import { ReactNode, useEffect, useRef, useState } from 'react';

import { cn } from '../../utils/cn';
import { Text } from '../Text';
import { Tooltip } from '../Tooltip';

export interface GroupedTabOption<T> {
  display: ReactNode;
  value: T;
  disabled?: boolean;
  tooltip?: string | ReactNode;
  icon?: ReactNode;
  className?: string;
  size?: 'sm' | 'md';
}

export type GroupedTabsProps<T extends string | number> = {
  value: T;
  onChange: (value: T) => void;
  options: GroupedTabOption<T>[];
  className?: string;
  size?: 'sm' | 'md';
  disabled?: boolean;
  tooltip?: string;
};

export const GroupedTabs = <T extends string | number>({
  value,
  onChange,
  options,
  className,
  size = 'sm',
  disabled,
  tooltip,
}: GroupedTabsProps<T>) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [indicatorStyle, setIndicatorStyle] = useState<{
    width: number;
    left: number;
  }>({ width: 0, left: 0 });

  // Update indicator position when value changes
  useEffect(() => {
    const activeIndex = options.findIndex((option) => option.value === value);
    const activeButton = buttonRefs.current[activeIndex];

    if (activeButton && containerRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect();
      const buttonRect = activeButton.getBoundingClientRect();

      setIndicatorStyle({
        width: buttonRect.width,
        left: buttonRect.left - containerRect.left,
      });
    }
  }, [value, options]);

  const isActive = (option: GroupedTabOption<T>) => option.value === value;
  return (
    <Tooltip title={tooltip}>
      <div
        ref={containerRef}
        className={cn(
          'relative flex w-fit rounded-md border border-secondary bg-background',
          className
        )}
      >
        {/* Animated background indicator */}
        <div
          className="absolute top-1 h-[calc(100%-8px)] rounded-[4px] bg-quaternary transition-all duration-200 ease-out"
          style={{
            width: indicatorStyle.width,
            transform: `translateX(${indicatorStyle.left}px)`,
          }}
        />

        {options.map((option, idx) => (
          <Tooltip key={option.value} title={option.tooltip}>
            <button
              ref={(el) => {
                buttonRefs.current[idx] = el;
              }}
              type="button"
              onClick={() => onChange(option.value)}
              disabled={option.disabled || disabled}
              className={cn(
                'relative flex items-center gap-1',
                'my-1 transition-colors duration-100',
                {
                  'rounded-xs px-2 py-0.5': size === 'sm',
                  'rounded-sm px-2 py-1': size === 'md',
                },
                {
                  'ml-1.5 mr-1': idx === 0,
                  'ml-1 mr-1.5': idx === options.length - 1,
                  'mx-0.5': idx !== 0 && idx !== options.length - 1,
                  'hover:bg-secondary-hover': !isActive(option),
                },
                (option.disabled || disabled) &&
                  'text-disabled hover:bg-transparent',
                option.className
              )}
            >
              {option.icon}
              {option.display && <Text variant={size}>{option.display}</Text>}
            </button>
          </Tooltip>
        ))}
      </div>
    </Tooltip>
  );
};
